import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import ClassNames from 'classnames';

import { getUniqueId } from '../../../helpers/getUniqueId';
import { events, types } from '../constants';
import { securePasswordTypesType } from '../../../shared/types';

require('./styles.css');

const getIframeParams = ({ id, type, sso_app, host, sso_port }) => {
  const url = new URL(window.location.href);
  const _host = host || url.host;
  const { port, origin: targetOrigin } = new URL(window.location.href);

  const origin = getIframeOrigin({ sso_app, sso_port, host: _host });
  const src = `${origin}/password-page?id=${id}&type=${type}&iframe=true${
    port ? `&port=${port}` : ''
  }&targetOrigin=${targetOrigin}`;

  return { src, origin };
};

const getIframeOrigin = ({ sso_app, host, sso_port }) => {
  return `https://${sso_app}.${host}${sso_port ? `:${sso_port}` : ''}`;
};

const HANDSHAKE_TIMEOUT = 5000;

class IframeWrapper extends Component {
  constructor(props) {
    super(props);

    const { id, type, sso_app, host, sso_port } = this.props;

    const { src, origin } = getIframeParams({ id, type, sso_app, host, sso_port });
    this.iframeSrc = src;
    this.iframeOrigin = origin;
    this.passwordIframeRef = createRef();
    this.messagesQueue = {};
    this.state = {
      isReady: false,
      isReload: false,
    };
  }

  componentDidMount() {
    window.addEventListener('message', this.receiveMessage, false);
  }

  componentDidUpdate() {
    this.reloadIframe();
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.receiveMessage, false);
  }

  reloadIframe() {
    if (this.state.isReload) {
      this.setState(state => ({
        ...state,
        isReload: false,
      }));
    }
  }

  handleIframeOnLoad = async () => {
    try {
      await this.postMessage({ action: events.HANDSHAKE.REQUEST, timeout: HANDSHAKE_TIMEOUT });
    } catch (e) {
      this.setState(state => ({
        ...state,
        isReady: false,
        isReload: true,
      }));
      this.props.onReadyChange({ isReady: false });
    }
  };

  validate = () => {
    return this.postMessage({
      action: events.VALIDATE.REQUEST,
    });
  };

  createAccount = ({
    email,
    globals = { host: '', version: '', url_params: {}, plid: '' },
    username,
    session_id,
    consent_preferences,
  }) => {
    const { type, onError } = this.props;

    if (type !== types.CREATE_ACCOUNT) {
      onError('Invalid type provided');
    }

    const data = {
      email,
      globals,
      username,
      session_id,
      consent_preferences,
    };

    return this.postMessage({
      action: events.CREATE_ACCOUNT.REQUEST,
      data,
    });
  };

  focus = value => {
    return this.postMessage({
      action: events.SET_FOCUS,
      data: value,
    });
  };

  login = ({
    email,
    globals = { url_params: {}, realm: '', plid: '', host: '' },
    username,
    remember_me,
    login_method,
    social_state_to_link_account,
  }) => {
    const { type, onError } = this.props;

    if (type !== types.LOGIN) {
      onError('Invalid type provided');
    }

    const data = {
      email,
      globals,
      username,
      remember_me,
      login_method,
      social_state_to_link_account,
    };
    return this.postMessage({
      action: events.LOGIN.REQUEST,
      data,
    });
  };

  showPassword = isShow => {
    return this.postMessage({ action: events.SHOW_PASSWORD, data: isShow });
  };

  postMessage = ({ action, data, timeout }) =>
    new Promise((resolve, reject) => {
      const { id: iframeId } = this.props;

      const id = getUniqueId();
      const message = { id, action, payload: data, iframeId };
      let timeoutId;

      if (timeout) {
        timeoutId = setTimeout(() => {
          reject(new Error(`Promise timed out after ${timeout} ms`));
        }, timeout);
      }

      this.passwordIframeRef?.current?.contentWindow?.postMessage(message, this.iframeOrigin);

      this.messagesQueue[id] = {
        resolve: result => {
          timeoutId && clearTimeout(timeoutId);
          resolve(result);
        },
        reject: error => reject(error),
      };
    });

  receiveMessage = event => {
    const { id: eventId, error, action, data, iframeId } = event?.data || {};
    const { id, onError, onReadyChange, onActive, onBlur, onFocus, onChange, onAutofill, onKeyPress } = this.props;

    if (iframeId !== id) {
      return;
    }

    if (this.messagesQueue[eventId]) {
      error ? this.messagesQueue[eventId].reject(error) : this.messagesQueue[eventId].resolve(data);
      delete this.messagesQueue[eventId];
    } else {
      switch (action) {
        case events.READY: {
          onReadyChange({
            data: {
              focus: this.focus,
              login: this.login,
              validate: this.validate,
              showPassword: this.showPassword,
              createAccount: this.createAccount,
            },
            isReady: true,
          });
          this.setState(state => ({ ...state, isReady: true }));
          break;
        }
        case events.AUTOFILL: {
          onAutofill(data);
          break;
        }
        case events.ERROR: {
          onError(error);
          break;
        }
        case events.INPUT.FOCUS: {
          onFocus();
          break;
        }
        case events.INPUT.BLUR: {
          onBlur();
          break;
        }
        case events.INPUT.ACTIVE: {
          onActive(data);
          break;
        }
        case events.INPUT.CHANGE: {
          onChange();
          break;
        }
        case events.INPUT.KEY_PRESS: {
          onKeyPress(data);
          break;
        }
      }
    }
  };

  render() {
    const { isReady, isReload } = this.state;
    const { useTextInputStyles } = this.props;

    const iframeClassNames = ClassNames({
      ready: isReady,
      'ux-text-entry-field': useTextInputStyles,
    });

    return isReload ? null : (
      // eslint-disable-next-line jsx-a11y/iframe-has-title
      <iframe
        id='password-frame'
        ref={this.passwordIframeRef}
        src={this.iframeSrc}
        onLoad={this.handleIframeOnLoad}
        className={iframeClassNames}
      />
    );
  }
}

IframeWrapper.defaultProps = {
  host: '',
  sso_app: 'origin-sso-ui',
  sso_port: '',
  onAutofill: () => {},
};

IframeWrapper.propTypes = {
  id: PropTypes.string.isRequired,
  host: PropTypes.string,
  type: securePasswordTypesType,
  onBlur: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  sso_app: PropTypes.string,
  onFocus: PropTypes.func.isRequired,
  sso_port: PropTypes.string,
  onActive: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onKeyPress: PropTypes.func.isRequired,
  onAutofill: PropTypes.func.isRequired,
  onReadyChange: PropTypes.func.isRequired,
  useTextInputStyles: PropTypes.bool,
};

export default IframeWrapper;
